import {Action, ButtonVariant} from 'platform/components';
import {match} from 'ts-pattern';

import {isEmpty, isNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {HeaderActionType, HeaderDynamicActionsResponse} from '@dms/api/warehouse';
import i18n from '@dms/i18n';

import {buildArray, Nullish} from 'shared/web';

type HeaderActionCallback = (key: string, type?: HeaderActionType) => void;

type CreateHeaderActionsConfig = {
  actions: HeaderDynamicActionsResponse['actions'] | Nullish;
  callback: HeaderActionCallback;
  isLoading?: boolean;
  isDisabled?: boolean;
};

const DEFAULT_ACTION_TYPE: HeaderActionType = 'BE';

/**
 * Creates header actions based on the provided configuration.
 * Actions are organized into three categories:
 * 1. Primary action buttons (from optionClick)
 * 2. Context menu actions (from remaining non-printout options)
 * 3. Printout actions (separate dropdown for type === 'PRINTOUT')
 *
 * @param {CreateHeaderActionsConfig} props - The configuration object for creating header actions.
 * @param {Object} props.actions - Actions configuration.
 * @param {Array<Object>} props.actions.options - List of action options available for the header.
 * @param {string|string[]} [props.actions.optionClick] - Key or array of keys representing primary actions to display as buttons.
 * @param {boolean} [props.isLoading] - Indicates whether the actions are in a loading state.
 * @param {Function} props.callback - Callback function invoked when an action is clicked. Receives key and type parameters.
 *
 * @returns {Action[]} An array of header actions consisting of:
 *   - Context menu dropdown (icon button with remaining non-printout actions, excluding hidden ones)
 *   - Printout dropdown (button with 'Generate Documents' label for printout actions)
 *   - Primary action buttons (derived from optionClick, rendered last)
 *
 * @example
 * Example usage with all action types:
 * const config = {
 *   actions: {
 *     options: [
 *       { key: 'edit', title: 'Edit', variant: 'primary', position: 1, type: 'BE' },
 *       { key: 'delete', title: 'Delete', variant: 'secondary', position: 2, type: 'BE' },
 *       { key: 'invoice', title: 'Invoice', position: 3, type: 'PRINTOUT' },
 *       { key: 'receipt', title: 'Receipt', position: 4, type: 'PRINTOUT' },
 *       { key: 'share', title: 'Share', style: 'hidden', position: 5, type: 'BE' },
 *     ],
 *     optionClick: ['edit'], // Primary button(s)
 *   },
 *   isLoading: false,
 *   callback: (key, type) => {
 *     console.log(`Action clicked: ${key}, Type: ${type}`);
 *   },
 * };
 *
 * const headerActions = createHeaderActions(config);
 * // Result: [context menu dropdown, printout dropdown, edit button]
 */
export const createHeaderActions = (props: CreateHeaderActionsConfig): Action[] => {
  if (isNil(props.actions) || isEmpty(props.actions.options)) {
    return [];
  }

  const optionClickKeys = match(props.actions.optionClick)
    .when(Array.isArray, (keys) => keys)
    .when(String, (key) => [key])
    .otherwise(() => []);

  const primaryActions = optionClickKeys
    .map((key) => props.actions?.options.find((option) => option.key === key))
    .filter((action): action is NonNullable<typeof action> => Boolean(action));

  const buttonsWithTooltip = primaryActions.map((action) => ({
    type: 'buttonWithTooltip',
    title: action.title ?? action.key ?? '',
    variant: (action.variant as ButtonVariant) ?? 'primary',
    isDisabled:
      action.key === 'discard'
        ? action.style === 'disabled'
        : action.style === 'disabled' || props.isDisabled === true,
    isLoading: props.isLoading ?? false,
    tooltip: {
      label: action.tooltip?.label,
      description: action.tooltip?.description,
    },
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      props.callback(action.key as string, action.type ?? DEFAULT_ACTION_TYPE);
    },
  }));

  const contextOptions = props.actions.options
    .filter((option) => !optionClickKeys.includes(option.key))
    .filter((option) => option.type !== 'PRINTOUT')
    .filter((option) => option.style !== 'hidden')
    .sort((a, b) => a.position - b.position);

  const printoutOptions = props.actions.options
    .filter((option) => !optionClickKeys.includes(option.key))
    .filter((option) => option.type === 'PRINTOUT')
    .filter((option) => option.style !== 'hidden')
    .sort((a, b) => a.position - b.position);

  return buildArray<Action>()
    .when(isNotNilOrEmpty(contextOptions), {
      type: 'dropdown-iconButton',
      icon: 'navigation/more_vert',
      priority: 'secondary',
      menuItems: contextOptions.map((contextOption) => ({
        variant: 'primary',
        isDisabled:
          contextOption.key === 'discard'
            ? contextOption.style === 'disabled'
            : contextOption.style === 'disabled' || props.isDisabled === true,
        isLoading: props.isLoading ?? false,
        label: contextOption.title ?? contextOption.key,
        tooltip: {
          label: contextOption.tooltip?.label,
          description: contextOption.tooltip?.description,
        },
        onClick: (event) => {
          event.stopPropagation();
          props.callback(contextOption.key, contextOption.type ?? DEFAULT_ACTION_TYPE);
        },
      })),
    })
    .when(isNotNilOrEmpty(printoutOptions), {
      type: 'dropdown-button',
      title: i18n.t('generals.labels.generateDocuments'),
      rightIcon: 'navigation/expand_more',
      variant: 'secondary',
      menuItems: printoutOptions.map((contextOption) => ({
        variant: 'primary',
        isDisabled:
          contextOption.key === 'discard'
            ? contextOption.style === 'disabled'
            : contextOption.style === 'disabled' || props.isDisabled === true,
        isLoading: props.isLoading ?? false,
        label: contextOption.title ?? contextOption.key,
        tooltip: {
          label: contextOption.tooltip?.label,
          description: contextOption.tooltip?.description,
        },
        onClick: (event) => {
          event.stopPropagation();
          props.callback(contextOption.key, contextOption.type ?? DEFAULT_ACTION_TYPE);
        },
      })),
    })
    .concat(buttonsWithTooltip as Action[]);
};
